import { useJobs, useMe, useSession } from "@/api/hooks";
import type { Job } from "@/api/hooks/useJobs";
import { createContext, useContext, useEffect } from "react";
import { usePush } from "./PushContexts";

interface JobsContextValue {
  jobs: Job[] | undefined;
  pendingJobs: number;
  activeJobId: string | null;
  isLoading: boolean;
  refetch: () => void;
}

const JobsContext = createContext<JobsContextValue | undefined>(undefined);

export function JobsProvider({ children }: React.PropsWithChildren) {
  const { data: me } = useMe();
  const { data: session } = useSession();
  const { lastNotification } = usePush();

  const enabled = Boolean(me && session);

  const { data: jobs, isLoading, refetch } = useJobs(enabled);

  const pendingJobs =
    jobs?.filter((j) => j["own-status"].state === "pending").length ?? 0;

  const activeJob = jobs?.find((j) =>
    ["started", "arrived"].includes(j["own-status"].state),
  );

  useEffect(() => {
    if (lastNotification) {
      refetch();
    }
  }, [lastNotification, refetch]);

  return (
    <JobsContext.Provider
      value={{
        jobs,
        pendingJobs,
        activeJobId: activeJob?.id ?? null,
        isLoading,
        refetch,
      }}
    >
      {children}
    </JobsContext.Provider>
  );
}

export function useJobsContext() {
  const ctx = useContext(JobsContext);
  if (!ctx) {
    throw new Error("useJobsContext must be used within JobsProvider");
  }
  return ctx;
}
